<template>
  <section id="portfolio" class="py-32 bg-white relative overflow-hidden">
    <div class="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent"></div>
    
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-16">
        <div class="inline-flex items-center px-5 py-2.5 bg-amber-100 rounded-full mb-8">
          <Award :size="20" class="mr-2 text-amber-600" />
          <span class="text-sm font-semibold text-amber-700">企业实力</span>
        </div>
        
        <h2 class="text-4xl sm:text-5xl font-bold font-display text-dark mb-6">
          实力铸就
          <span class="text-amber-600">品质保障</span>
        </h2>
        
        <p class="text-lg text-gray-600 max-w-3xl mx-auto">
          我们拥有先进的生产设备、专业的技术团队和完善的质量管理体系，确保为客户提供稳定可靠的产品。
        </p>
      </div>
      
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
        <div v-for="(stat, index) in stats" :key="stat.label" 
             class="text-center p-8 bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl group hover:shadow-xl transition-all duration-500 hover:-translate-y-2"
             :style="{ animationDelay: `${index * 100}ms` }">
          <div class="w-20 h-20 mx-auto bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500">
            <component :is="stat.icon" :size="40" class="text-white" />
          </div>
          <div class="text-4xl font-bold text-amber-600 mb-2">{{ stat.value }}</div>
          <div class="text-gray-600">{{ stat.label }}</div>
        </div>
      </div>
      
      <div class="grid md:grid-cols-2 gap-8">
        <div class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8">
          <h3 class="text-2xl font-bold font-display text-dark mb-6">资质认证</h3>
          <div class="grid grid-cols-2 gap-6">
            <div v-for="cert in certifications" :key="cert.name" 
                 class="bg-white rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
              <component :is="cert.icon" :size="48" class="mx-auto text-amber-500 mb-4" />
              <div class="font-semibold text-gray-800">{{ cert.name }}</div>
            </div>
          </div>
        </div>
        
        <div class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-3xl p-8">
          <h3 class="text-2xl font-bold font-display text-dark mb-6">生产流程</h3>
          <div class="space-y-4">
            <div v-for="(step, index) in productionSteps" :key="step.title" 
                 class="flex items-start gap-4">
              <div class="w-10 h-10 min-w-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold shrink-0">
                {{ index + 1 }}
              </div>
              <div>
                <div class="font-semibold text-gray-800 mb-1">{{ step.title }}</div>
                <div class="text-sm text-gray-600">{{ step.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="mt-16">
        <h3 class="text-2xl font-bold font-display text-dark text-center mb-10">合作客户</h3>
        <div class="flex flex-wrap justify-center items-center gap-8 opacity-60">
          <div v-for="client in clients" :key="client" class="text-2xl font-bold text-gray-400 hover:text-amber-600 transition-colors">
            {{ client }}
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { Award, Factory, Users, Truck, Shield, FileCheck, BadgeCheck, ClipboardCheck, CheckSquare } from 'lucide-vue-next'
import { markRaw } from 'vue'

const stats = [
  { icon: markRaw(Factory), value: '20000m²', label: '生产基地' },
  { icon: markRaw(Users), value: '200+', label: '专业员工' },
  { icon: markRaw(Truck), value: '500+', label: '合作企业' },
  { icon: markRaw(Shield), value: '99.8%', label: '产品合格率' }
]

const certifications = [
  { name: 'ISO9001', icon: markRaw(FileCheck) },
  { name: 'HACCP', icon: markRaw(BadgeCheck) },
  { name: '食品安全', icon: markRaw(ClipboardCheck) },
  { name: 'SGS检测', icon: markRaw(CheckSquare) }
]

const productionSteps = [
  { title: '原料采购', description: '精选优质原料，严格把控源头质量' },
  { title: '精炼加工', description: '采用先进工艺，多道工序精炼' },
  { title: '质量检测', description: '专业检测设备，确保产品合格' },
  { title: '成品包装', description: '无菌包装车间，安全卫生' }
]

const clients = ['海底捞', '呷哺呷哺', '杨国福', '小龙坎', '德庄', '刘一手']
</script>
